import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { API_BASE, SITE_BASE_URL } from '../../config/environments/api';
import './AllProjectsPage.scss';

const AllProjectsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Initialize selectedOrg from URL params if available
  const [selectedOrg, setSelectedOrg] = useState(() => {
    const orgParam = searchParams.get('org');
    return orgParam || 'All';
  });
  // Initialize selectedCountry from URL params if available
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const countryParam = searchParams.get('country');
    return countryParam || 'All';
  });
  // Initialize selectedSegment from URL params if available
  const [selectedSegment, setSelectedSegment] = useState(() => {
    const segmentParam = searchParams.get('segment');
    return segmentParam || 'All';
  });
  const [countries, setCountries] = useState([]);
  const [segments, setSegments] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
    limit: 10
  });
  const [currentPage, setCurrentPage] = useState(1);
  // Track if we're updating from URL to prevent loops
  const isUpdatingFromUrlRef = useRef(false);

  // Update selectedCountry, selectedOrg, and selectedSegment when URL params change
  useEffect(() => {
    const countryParam = searchParams.get('country');
    const orgParam = searchParams.get('org');
    const segmentParam = searchParams.get('segment');
    
    isUpdatingFromUrlRef.current = true;
    
    if (countryParam) {
      setSelectedCountry(countryParam);
    } else {
      setSelectedCountry('All');
    }
    
    if (orgParam) {
      setSelectedOrg(orgParam);
    } else {
      setSelectedOrg('All');
    }
    
    if (segmentParam) {
      setSelectedSegment(segmentParam);
    } else {
      setSelectedSegment('All');
    }
    
    // Reset to page 1 when filters change from URL
    setCurrentPage(1);
    
    // Reset flag after state updates
    setTimeout(() => {
      isUpdatingFromUrlRef.current = false;
    }, 0);
  }, [searchParams]);

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${API_BASE}/projects/countries`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        if (result.success) {
          setCountries(result.data || []);
        }
      } catch (err) {
        // Error fetching countries - silently fail
      }
    };
    
    fetchCountries();
  }, []);

  // Fetch segments dynamically based on selected country
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        // Build URL with country parameter if a specific country is selected
        let url = `${API_BASE}/projects/segments`;
        if (selectedCountry && selectedCountry !== 'All') {
          url += `?country=${encodeURIComponent(selectedCountry)}`;
        }
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        if (result.success) {
          const newSegments = result.data || [];
          setSegments(newSegments);
          
          // If current selected segment is not in the new segments list, reset to 'All'
          setSelectedSegment(prevSegment => {
            if (prevSegment !== 'All' && !newSegments.includes(prevSegment)) {
              return 'All';
            }
            return prevSegment;
          });
        }
      } catch (err) {
        // Error fetching segments - silently fail
      }
    };
    
    fetchSegments();
  }, [selectedCountry]);

  // Fetch projects with filters using GET endpoint with query parameters
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.set('page', currentPage.toString());
      queryParams.set('limit', '10');

      // Add reporting_org filter
      if (selectedOrg !== 'All') {
        if (selectedOrg === 'World Bank') {
          queryParams.set('reporting_org', 'World Bank');
        } else if (selectedOrg === 'AFDB') {
          queryParams.set('reporting_org', 'AFDB');
        }
      }

      // Add country filter
      if (selectedCountry !== 'All') {
        queryParams.set('country', selectedCountry);
      }

      // Add segment filter
      if (selectedSegment !== 'All') {
        queryParams.set('segments', selectedSegment);
      }

      const response = await fetch(`${API_BASE}/projects/all?${queryParams.toString()}`);

      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.text();
        } catch (e) {
          errorText = `Failed to read error response: ${e.message}`;
        }
        
        // Try to parse as JSON if possible
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch {
          // If not JSON, use the text
          if (errorText && !errorText.includes('<!DOCTYPE')) {
            errorMessage = errorText;
          }
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        setProjects(result.data || []);
        if (result.pagination) {
          setPagination(result.pagination);
        }
      } else {
        setError(result.message || 'Failed to fetch projects');
      }
    } catch (err) {
      setError(`Failed to load projects: ${err.message}`);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, selectedCountry, selectedSegment, currentPage]);

  // Update URL params when filters change (but not when updating from URL)
  useEffect(() => {
    // Skip if we're updating from URL to prevent infinite loops
    if (isUpdatingFromUrlRef.current) {
      return;
    }
    
    const newSearchParams = new URLSearchParams();
    
    if (selectedCountry !== 'All') {
      newSearchParams.set('country', selectedCountry);
    }
    
    if (selectedOrg !== 'All') {
      newSearchParams.set('org', selectedOrg);
    }
    
    if (selectedSegment !== 'All') {
      newSearchParams.set('segment', selectedSegment);
    }
    
    // Only update URL if params actually changed
    const currentCountry = searchParams.get('country') || 'All';
    const currentOrg = searchParams.get('org') || 'All';
    const currentSegment = searchParams.get('segment') || 'All';
    
    if (selectedCountry !== currentCountry || selectedOrg !== currentOrg || selectedSegment !== currentSegment) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [selectedOrg, selectedCountry, selectedSegment, searchParams, setSearchParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrg, selectedCountry, selectedSegment]);

  // Fetch projects when filters or page changes
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return 'N/A';
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(2)}K`;
    }
    return `$${amount.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle YYYY-MM-DD format from API
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      }
      // Handle other date formats
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      }
      return 'N/A';
    } catch {
      return 'N/A';
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>All Projects | Africa Energy Trends</title>
        <meta name="description" content="Browse all energy projects across Africa with filters for World Bank, AFDB, and countries." />
        <meta property="og:title" content="All Projects | Africa Energy Trends" />
        <meta property="og:description" content="Browse all energy projects across Africa with filters for World Bank, AFDB, and countries." />
        <meta property="og:image" content={`${SITE_BASE_URL}/og-images/homepage.png`} />
        <meta property="og:url" content={`${SITE_BASE_URL}/projects`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="All Projects | Africa Energy Trends" />
        <meta name="twitter:description" content="Browse all energy projects across Africa with filters for World Bank, AFDB, and countries." />
      </Helmet>

      <div className="all-projects-page">
        <div className="container">
          <div className="page-header">
            <h1 className="page-title">All Projects</h1>
            <p className="page-subtitle">
              Explore comprehensive listings of energy projects across Africa
            </p>
          </div>

          <div className="filters-section">
            <div className="filters-row">
              <div className="filter-group">
                <label className="filter-label">Organization</label>
                <div className="filter-buttons">
                  <button
                    className={`filter-btn ${selectedOrg === 'All' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedOrg('All');
                    }}
                  >
                    All
                  </button>
                  <button
                    className={`filter-btn ${selectedOrg === 'World Bank' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedOrg('World Bank');
                    }}
                  >
                    World Bank
                  </button>
                  <button
                    className={`filter-btn ${selectedOrg === 'AFDB' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedOrg('AFDB');
                    }}
                  >
                    AFDB
                  </button>
                </div>
              </div>

              <div className="filter-group country-filter-group">
                <label className="filter-label">Country</label>
                <select
                  className="country-select"
                  value={selectedCountry}
                  onChange={(e) => {
                    setSelectedCountry(e.target.value);
                  }}
                >
                  <option value="All">All Countries</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group segment-filter-group">
                <label className="filter-label">Segment</label>
                <select
                  className="segment-select"
                  value={selectedSegment}
                  onChange={(e) => {
                    setSelectedSegment(e.target.value);
                  }}
                >
                  <option value="All">All Segments</option>
                  {segments.map((segment) => (
                    <option key={segment} value={segment}>
                      {segment}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {!loading && (
              <div className="results-count">
                Showing {projects.length} of {pagination.total} projects
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading projects...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="retry-btn" onClick={fetchProjects}>
                Retry
              </button>
            </div>
          ) : projects.length === 0 ? (
            <div className="empty-state">
              <p>No projects found matching your filters.</p>
              <button
                className="clear-filters-btn"
                onClick={() => {
                  setSelectedOrg('All');
                  setSelectedCountry('All');
                  setSelectedSegment('All');
                  setSearchParams({}, { replace: true });
                }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="projects-table-container">
                <table className="projects-table">
                  <thead>
                    <tr>
                      <th>Project Title</th>
                      <th>Country</th>
                      <th>Organization</th>
                      <th>Segment</th>
                      <th>Total Commitments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project, index) => (
                      <tr key={project._id || index}>
                        <td className="project-title">{project.title || 'N/A'}</td>
                        <td className="country">{project.country || 'N/A'}</td>
                        <td className="organization">
                          {project.reporting_org || 'N/A'}
                        </td>
                        <td className="segment">{project.segments || 'N/A'}</td>
                        <td className="commitments">
                          {formatCurrency(project.total_commitments)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.pages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <div className="pagination-info">
                    Page {pagination.current} of {pagination.pages}
                  </div>
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AllProjectsPage;

